/**
 * A clip store keyed by what was spoken. `fail` lets a refused fetch be
 * retried; `failed` is the caller's own notice of that refusal, since a
 * caller who joined someone else's in-flight fetch (`manyThenReplay`) has no
 * `fail` of its own to learn from.
 */
export type WavCache<T> = {
  get(
    key: string,
    fetch: (store: (v: T) => void, fail: () => void) => void,
    use: (v: T) => void,
    failed?: () => void,
  ): void;
};

/**
 * Fetch once, then replay (kid-practice §4.4).
 *
 * The text behind a `speak` id cannot change while a card is open, so a
 * second tap replays the first answer. A child hears the identical model
 * every time rather than a fresh synthesis with different prosody, and thirty
 * taps spend one synthesis of `SPEAK_PER_ACCOUNT`, not thirty. Keyed on the
 * target, so a button reused for another row never replays the old one.
 */
export function onceThenReplay<T>(): WavCache<T> {
  let cached: { key: string; value: T } | null = null;
  return {
    // `failed` is the caller's own notice of a refusal (final review I1): a
    // `HearIt` outside the reader passes no `cache` prop, so it runs on one
    // of these, and its `onError` sets the error code that `failed` reads.
    get(key, fetch, use, failed) {
      if (cached?.key === key) return use(cached.value);
      fetch(
        (value) => {
          cached = { key, value };
          use(value);
        },
        () => failed?.(),
      );
    },
  };
}

/**
 * Every key kept, for a book (storybooks PRD R6). Each page's clip lasts as
 * long as the book is open, so turning back replays rather than
 * re-synthesising. A prefetch still in flight is joined rather than
 * duplicated. A failed fetch keeps nothing, so the next tap tries again
 * instead of waiting on an answer that is never coming -- and every joined
 * caller is told, not only the one whose own `get` happened to start the
 * fetch, since a joiner has no `fail` of its own to learn from otherwise.
 */
export function manyThenReplay<T>(): WavCache<T> {
  const done = new Map<string, T>();
  const waiting = new Map<string, Array<{ use: (v: T) => void; failed?: () => void }>>();
  return {
    get(key, fetch, use, failed) {
      if (done.has(key)) return use(done.get(key) as T);
      const queue = waiting.get(key);
      if (queue) {
        queue.push({ use, failed });
        return;
      }
      waiting.set(key, [{ use, failed }]);
      fetch(
        (value) => {
          done.set(key, value);
          const waiters = waiting.get(key) ?? [];
          waiting.delete(key);
          waiters.forEach((w) => w.use(value));
        },
        () => {
          const waiters = waiting.get(key) ?? [];
          waiting.delete(key);
          waiters.forEach((w) => w.failed?.());
        },
      );
    },
  };
}

/** `speak`'s one named field and its id, as one cache key. */
export function targetKey(target: Record<string, string>): string {
  const [field, id] = Object.entries(target)[0];
  return `${field}:${id}`;
}
